import React, { useEffect, useMemo, useState, type JSX } from "react";
import { Navigate, RouterProvider, createBrowserRouter, type RouteObject } from "react-router-dom";
import {
  createAdminDashboardDependencies,
  type CreateAdminDashboardDependenciesOptions,
} from "./composition/create-admin-dashboard-dependencies";
import {
  resolveAdminDashboardRuntimeConfig,
  type AdminDashboardRuntimeConfig,
} from "./composition/admin-dashboard-runtime-config";
import { AdminShell } from "./layouts/admin-shell";
import { createDeviceKpisRoute } from "./routes/device-kpis.route";
import { createFailedFlowsRoute } from "./routes/failed-flows.route";
import { createJobsRoute } from "./routes/jobs.route";
import { createOverviewRoute } from "./routes/overview.route";
import { createReleasesRoute } from "./routes/releases.route";
import { createUserDetailRoute } from "./routes/user-detail.route";
import { createUsersRoute } from "./routes/users.route";
import { ADMIN_ROLES, type AdminRole } from "../contracts/admin-permissions";
import { AdminErrorState } from "../shared/ui/admin-error-state";
import { AdminAuthSessionBootstrapError, validateAdminAuthSessionConfig } from "../shared/auth/admin-auth-session";
import {
  createFirebaseWebAdminAuthProvider,
  type FirebaseAdminAuthSnapshot,
  type FirebaseWebAdminAuthProvider,
} from "../shared/auth/firebase-web-admin-auth-provider";
import {
  persistAdminDashboardLocale,
  resolveAdminDashboardLocale,
  type AdminDashboardLocale,
} from "../shared/locale/admin-dashboard-locale";
import { AdminDashboardLocaleProvider } from "../shared/locale/admin-dashboard-locale-context";
import { pickAdminDashboardLocaleText } from "../shared/locale/admin-dashboard-copy";
import { useAdminDashboardLocale } from "../shared/locale/admin-dashboard-locale-context";

export type AdminDashboardAppProps = {
  runtimeConfig?: Partial<AdminDashboardRuntimeConfig>;
  fixtureSession?: CreateAdminDashboardDependenciesOptions["fixtureSession"];
  getAccessToken?: CreateAdminDashboardDependenciesOptions["getAccessToken"];
  bootstrapError?: AdminDashboardBootstrapError;
};

export type AdminRouteObject = RouteObject;

export type AdminDashboardBootstrapError = {
  code:
    | "ADMIN_BOOTSTRAP_PAYLOAD_INVALID"
    | "ADMIN_AUTH_BOOTSTRAP_INVALID"
    | "ADMIN_AUTH_PROVIDER_CONFIG_INVALID";
  title: string;
  message: string;
  details?: string[];
};

function getWindowStorage(): Storage | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }

  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

function resolveInitialLocaleState(): AdminDashboardLocale {
  if (typeof window === "undefined") {
    return "ko";
  }

  return resolveAdminDashboardLocale({
    search: window.location.search,
    storage: getWindowStorage(),
  }).locale;
}

function isAccessTokenProvider(
  value: unknown,
): value is NonNullable<AdminDashboardAppProps["getAccessToken"]> {
  return typeof value === "function";
}

export function createAdminDashboardRoutes(dependencies: ReturnType<typeof createAdminDashboardDependencies>): AdminRouteObject[] {
  return [
    {
      path: "/admin",
      element: <AdminShell dependencies={dependencies} />,
      children: [
        createOverviewRoute(dependencies),
        createFailedFlowsRoute(dependencies),
        createDeviceKpisRoute(dependencies),
        createUsersRoute(dependencies),
        createUserDetailRoute(dependencies),
        createJobsRoute(dependencies),
        createReleasesRoute(dependencies),
      ],
    },
    {
      path: "*",
      element: <Navigate to="/admin" replace />,
    },
  ];
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function createBootstrapError(
  code: AdminDashboardBootstrapError["code"],
  title: string,
  message: string,
  details?: string[],
): AdminDashboardAppProps {
  return {
    bootstrapError: {
      code,
      title,
      message,
      details,
    },
  };
}

type LocalizedBootstrapErrorDisplay = {
  body: string;
  detailLabel: string;
  reasonLabel: string;
  title: string;
};

function localizeBootstrapErrorMessage(
  error: AdminDashboardBootstrapError,
  locale: AdminDashboardLocale,
): string {
  if (error.code === "ADMIN_BOOTSTRAP_PAYLOAD_INVALID") {
    if (error.message === "Host shell bootstrap must inject an object with runtimeConfig and optional providers.") {
      return pickAdminDashboardLocaleText(locale, {
        en: "Host shell bootstrap must inject an object with runtimeConfig and optional providers.",
        ko: "호스트 셸 부트스트랩은 실행 설정과 선택적 인증 공급자 정보를 포함한 객체를 주입해야 합니다.",
      });
    }

    if (error.message === "runtimeConfig must be an object when provided.") {
      return pickAdminDashboardLocaleText(locale, {
        en: "runtimeConfig must be an object when provided.",
        ko: "실행 설정을 제공할 때는 객체 형태여야 합니다.",
      });
    }

    if (error.message === "fixtureSession must be an object when provided.") {
      return pickAdminDashboardLocaleText(locale, {
        en: "fixtureSession must be an object when provided.",
        ko: "고정 세션 값을 제공할 때는 객체 형태여야 합니다.",
      });
    }

    if (error.message === "fixtureSession.roles must be an array when provided.") {
      return pickAdminDashboardLocaleText(locale, {
        en: "fixtureSession.roles must be an array when provided.",
        ko: "고정 세션 역할 목록을 제공할 때는 배열 형태여야 합니다.",
      });
    }

    if (error.message === "getAccessToken must be a function when provided.") {
      return pickAdminDashboardLocaleText(locale, {
        en: "getAccessToken must be a function when provided.",
        ko: "액세스 토큰 조회기를 제공할 때는 함수 형태여야 합니다.",
      });
    }
  }

  if (error.code === "ADMIN_AUTH_PROVIDER_CONFIG_INVALID") {
    if (error.message === "Standalone Firebase admin auth requires the full EXPO_PUBLIC_FIREBASE_* runtime config.") {
      return pickAdminDashboardLocaleText(locale, {
        en: "Standalone Firebase admin auth requires the full EXPO_PUBLIC_FIREBASE_* runtime config.",
        ko: "별도 Firebase 관리자 인증에는 전체 Firebase 공개 실행 설정이 필요합니다.",
      });
    }

    if (
      error.message ===
      "Standalone Cloudflare Pages admin deploys must set VITE_ADMIN_API_BASE_URL to the Cloud Run service root."
    ) {
      return pickAdminDashboardLocaleText(locale, {
        en: "Standalone Cloudflare Pages admin deploys must set VITE_ADMIN_API_BASE_URL to the Cloud Run service root.",
        ko: "별도 Cloudflare Pages 관리자 배포는 관리자 API 기준 주소를 Cloud Run 서비스 루트로 설정해야 합니다.",
      });
    }
  }

  if (
    error.code === "ADMIN_AUTH_BOOTSTRAP_INVALID" &&
    error.message === "Admin auth bootstrap requires an in-memory getAccessToken provider."
  ) {
    return pickAdminDashboardLocaleText(locale, {
      en: "Admin auth bootstrap requires an in-memory getAccessToken provider.",
      ko: "관리자 인증 부트스트랩에는 메모리 내 액세스 토큰 조회기가 필요합니다.",
    });
  }

  return error.message;
}

function localizeBootstrapErrorDetail(
  detail: string,
  locale: AdminDashboardLocale,
): string {
  if (detail === "Pages host itself does not proxy /api/admin/* in the current production contract.") {
    return pickAdminDashboardLocaleText(locale, {
      en: "Pages host itself does not proxy /api/admin/* in the current production contract.",
      ko: "현재 운영 계약에서는 Pages 호스트 자체가 /api/admin/* 를 프록시하지 않습니다.",
    });
  }

  if (detail === "Set VITE_ADMIN_API_BASE_URL to the deployed API service root without the /api suffix.") {
    return pickAdminDashboardLocaleText(locale, {
      en: "Set VITE_ADMIN_API_BASE_URL to the deployed API service root without the /api suffix.",
      ko: "관리자 API 기준 주소는 /api 접미사 없이 배포된 API 서비스 루트로 설정하세요.",
    });
  }

  if (
    detail ===
    "Required: EXPO_PUBLIC_FIREBASE_API_KEY, EXPO_PUBLIC_FIREBASE_APP_ID, EXPO_PUBLIC_FIREBASE_PROJECT_ID, EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN, EXPO_PUBLIC_FIREBASE_MESSAGING_SENDER_ID, EXPO_PUBLIC_FIREBASE_STORAGE_BUCKET"
  ) {
    return pickAdminDashboardLocaleText(locale, {
      en: detail,
      ko: "필수 공개 실행 설정: EXPO_PUBLIC_FIREBASE_API_KEY, EXPO_PUBLIC_FIREBASE_APP_ID, EXPO_PUBLIC_FIREBASE_PROJECT_ID, EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN, EXPO_PUBLIC_FIREBASE_MESSAGING_SENDER_ID, EXPO_PUBLIC_FIREBASE_STORAGE_BUCKET",
    });
  }

  if (detail === "Host shell must inject runtimeConfig and an in-memory getAccessToken provider together.") {
    return pickAdminDashboardLocaleText(locale, {
      en: detail,
      ko: "호스트 셸은 실행 설정과 메모리 내 액세스 토큰 조회기를 함께 주입해야 합니다.",
    });
  }

  return detail;
}

function buildLocalizedBootstrapErrorMeta(
  error: AdminDashboardBootstrapError,
  locale: AdminDashboardLocale,
): Array<{ key: string; label: string; value: string }> {
  const display = resolveBootstrapErrorDisplay(error, locale);

  return [
    {
      key: "reason",
      label: display.reasonLabel,
      value: localizeBootstrapErrorMessage(error, locale),
    },
    ...(error.details?.map((detail, index) => ({
      key: `detail_${index}`,
      label: display.detailLabel,
      value: localizeBootstrapErrorDetail(detail, locale),
    })) ?? []),
  ];
}

export function resolveBootstrapErrorDisplay(
  error: AdminDashboardBootstrapError,
  locale: AdminDashboardLocale,
): LocalizedBootstrapErrorDisplay {
  if (error.code === "ADMIN_BOOTSTRAP_PAYLOAD_INVALID") {
    return {
      title: pickAdminDashboardLocaleText(locale, {
        en: "Admin bootstrap payload invalid",
        ko: "관리자 부트스트랩 입력값 오류",
      }),
      body: pickAdminDashboardLocaleText(locale, {
        en: "The host shell injected an invalid admin dashboard bootstrap payload.",
        ko: "호스트 셸이 올바르지 않은 관리자 대시보드 부트스트랩 입력값을 주입했습니다.",
      }),
      reasonLabel: pickAdminDashboardLocaleText(locale, {
        en: "Reason",
        ko: "사유",
      }),
      detailLabel: pickAdminDashboardLocaleText(locale, {
        en: "Detail",
        ko: "상세",
      }),
    };
  }

  if (error.code === "ADMIN_AUTH_PROVIDER_CONFIG_INVALID") {
    return {
      title: pickAdminDashboardLocaleText(locale, {
        en: "Admin auth provider configuration missing",
        ko: "관리자 인증 공급자 설정 누락",
      }),
      body: pickAdminDashboardLocaleText(locale, {
        en: "Standalone admin auth configuration is incomplete, so the dashboard cannot open the real service session boundary.",
        ko: "독립형 관리자 인증 설정이 완전하지 않아 대시보드가 실서비스 세션 경계를 열 수 없습니다.",
      }),
      reasonLabel: pickAdminDashboardLocaleText(locale, {
        en: "Reason",
        ko: "사유",
      }),
      detailLabel: pickAdminDashboardLocaleText(locale, {
        en: "Detail",
        ko: "상세",
      }),
    };
  }

  return {
    title: pickAdminDashboardLocaleText(locale, {
      en: "Admin auth bootstrap required",
      ko: "관리자 인증 부트스트랩 필요",
    }),
    body: pickAdminDashboardLocaleText(locale, {
      en: "The host shell must provide the admin auth bootstrap contract before the dashboard can render live routes.",
      ko: "대시보드가 실서비스 화면을 렌더링하려면 호스트 셸이 관리자 인증 부트스트랩 계약을 먼저 제공해야 합니다.",
    }),
    reasonLabel: pickAdminDashboardLocaleText(locale, {
      en: "Reason",
      ko: "사유",
    }),
    detailLabel: pickAdminDashboardLocaleText(locale, {
      en: "Detail",
      ko: "상세",
    }),
  };
}

type LocalizedFirebaseAuthScreenCopy = {
  authModeLabel: string;
  authModeValue: string;
  body: string;
  rawReasonLabel: string;
  resetSession: string;
  sessionLabel: string;
  sessionStateValue: string;
  signInAction: string;
  signingInAction: string;
  title: string;
};

export function localizeFirebaseAuthReason(
  message: string,
  locale: AdminDashboardLocale,
): string {
  if (message === "Failed to initialize Firebase admin auth.") {
    return pickAdminDashboardLocaleText(locale, {
      en: message,
      ko: "Firebase 관리자 인증 초기화에 실패했습니다.",
    });
  }

  if (message === "Failed to resolve admin Firebase session.") {
    return pickAdminDashboardLocaleText(locale, {
      en: message,
      ko: "Firebase 관리자 세션 상태를 확인하지 못했습니다.",
    });
  }

  if (message === "Google popup sign-in failed.") {
    return pickAdminDashboardLocaleText(locale, {
      en: message,
      ko: "Google 로그인 창 인증에 실패했습니다.",
    });
  }

  if (message === "Firebase sign-out failed.") {
    return pickAdminDashboardLocaleText(locale, {
      en: message,
      ko: "Firebase 로그아웃 처리에 실패했습니다.",
    });
  }

  return message;
}

export function resolveFirebaseAuthScreenCopy(
  snapshot: FirebaseAdminAuthSnapshot,
  locale: AdminDashboardLocale,
): LocalizedFirebaseAuthScreenCopy {
  if (snapshot.status === "error") {
    return {
      title: pickAdminDashboardLocaleText(locale, {
        en: "Admin sign-in required",
        ko: "관리자 로그인 필요",
      }),
      body: pickAdminDashboardLocaleText(locale, {
        en: "Google popup admin sign-in did not complete. Review the raw provider reason and retry the session bootstrap.",
        ko: "Google 관리자 로그인 창이 끝나지 않았습니다. 원본 사유를 확인한 뒤 세션 부트스트랩을 다시 시도하세요.",
      }),
      authModeLabel: pickAdminDashboardLocaleText(locale, {
        en: "Auth mode",
        ko: "인증 방식",
      }),
      authModeValue: pickAdminDashboardLocaleText(locale, {
        en: "Firebase Google popup",
        ko: "Google 관리자 로그인",
      }),
      sessionLabel: pickAdminDashboardLocaleText(locale, {
        en: "Session",
        ko: "세션",
      }),
      sessionStateValue: pickAdminDashboardLocaleText(locale, {
        en: "Error",
        ko: "오류",
      }),
      signInAction: pickAdminDashboardLocaleText(locale, {
        en: "Sign in with Google",
        ko: "Google로 로그인",
      }),
      signingInAction: pickAdminDashboardLocaleText(locale, {
        en: "Signing in...",
        ko: "로그인 중...",
      }),
      resetSession: pickAdminDashboardLocaleText(locale, {
        en: "Reset session",
        ko: "세션 초기화",
      }),
      rawReasonLabel: pickAdminDashboardLocaleText(locale, {
        en: "Raw reason",
        ko: "원본 사유",
      }),
    };
  }

  const hasNoRoles = snapshot.status === "signed_in" && snapshot.roles.length === 0;
  if (hasNoRoles) {
    return {
      title: pickAdminDashboardLocaleText(locale, {
        en: "Admin role missing",
        ko: "관리자 역할 누락",
      }),
      body: pickAdminDashboardLocaleText(locale, {
        en: "The signed-in account does not have a Firebase admin custom claim for this dashboard.",
        ko: "로그인한 계정에 이 대시보드용 Firebase 관리자 권한 클레임이 없습니다.",
      }),
      authModeLabel: pickAdminDashboardLocaleText(locale, {
        en: "Auth mode",
        ko: "인증 방식",
      }),
      authModeValue: pickAdminDashboardLocaleText(locale, {
        en: "Firebase Google popup",
        ko: "Google 관리자 로그인",
      }),
      sessionLabel: pickAdminDashboardLocaleText(locale, {
        en: "Session",
        ko: "세션",
      }),
      sessionStateValue: pickAdminDashboardLocaleText(locale, {
        en: "Signed in without admin role",
        ko: "관리자 역할 없이 로그인됨",
      }),
      signInAction: pickAdminDashboardLocaleText(locale, {
        en: "Sign in with Google",
        ko: "Google로 로그인",
      }),
      signingInAction: pickAdminDashboardLocaleText(locale, {
        en: "Signing in...",
        ko: "로그인 중...",
      }),
      resetSession: pickAdminDashboardLocaleText(locale, {
        en: "Reset session",
        ko: "세션 초기화",
      }),
      rawReasonLabel: pickAdminDashboardLocaleText(locale, {
        en: "Claim boundary",
        ko: "클레임 경계",
      }),
    };
  }

  return {
    title: pickAdminDashboardLocaleText(locale, {
      en: "Admin sign-in required",
      ko: "관리자 로그인 필요",
    }),
    body: pickAdminDashboardLocaleText(locale, {
      en: "Sign in with the PetLog admin Google account to open the dashboard.",
      ko: "대시보드를 열려면 PetLog 관리자 Google 계정으로 로그인하세요.",
    }),
    authModeLabel: pickAdminDashboardLocaleText(locale, {
      en: "Auth mode",
      ko: "인증 방식",
    }),
      authModeValue: pickAdminDashboardLocaleText(locale, {
        en: "Firebase Google popup",
        ko: "Google 관리자 로그인",
      }),
    sessionLabel: pickAdminDashboardLocaleText(locale, {
      en: "Session",
      ko: "세션",
    }),
    sessionStateValue: pickAdminDashboardLocaleText(locale, {
      en: "Signed out",
      ko: "로그아웃됨",
    }),
    signInAction: pickAdminDashboardLocaleText(locale, {
      en: "Sign in with Google",
      ko: "Google로 로그인",
    }),
    signingInAction: pickAdminDashboardLocaleText(locale, {
      en: "Signing in...",
      ko: "로그인 중...",
    }),
    resetSession: pickAdminDashboardLocaleText(locale, {
      en: "Reset session",
      ko: "세션 초기화",
    }),
    rawReasonLabel: pickAdminDashboardLocaleText(locale, {
      en: "Raw reason",
      ko: "원본 사유",
    }),
  };
}

function normalizeFixtureSession(value: unknown): AdminDashboardAppProps["fixtureSession"] | undefined {
  if (value === undefined) return undefined;
  if (!isObject(value)) return undefined;
  if (!("roles" in value) || value.roles === undefined) return {};
  if (!Array.isArray(value.roles)) return undefined;

  const roles = value.roles.filter((item): item is AdminRole => typeof item === "string" && ADMIN_ROLES.includes(item as AdminRole));
  return roles.length > 0 ? { roles } : {};
}

export function parseAdminDashboardBootstrapInput(raw: unknown): AdminDashboardAppProps | undefined {
  if (raw === undefined) {
    return undefined;
  }

  if (!isObject(raw)) {
    return createBootstrapError(
      "ADMIN_BOOTSTRAP_PAYLOAD_INVALID",
      "Admin bootstrap payload invalid",
      "Host shell bootstrap must inject an object with runtimeConfig and optional providers.",
    );
  }

  if ("runtimeConfig" in raw && raw.runtimeConfig !== undefined && !isObject(raw.runtimeConfig)) {
    return createBootstrapError(
      "ADMIN_BOOTSTRAP_PAYLOAD_INVALID",
      "Admin bootstrap payload invalid",
      "runtimeConfig must be an object when provided.",
    );
  }

  if ("fixtureSession" in raw && raw.fixtureSession !== undefined && !isObject(raw.fixtureSession)) {
    return createBootstrapError(
      "ADMIN_BOOTSTRAP_PAYLOAD_INVALID",
      "Admin bootstrap payload invalid",
      "fixtureSession must be an object when provided.",
    );
  }

  if (
    isObject(raw.fixtureSession) &&
    "roles" in raw.fixtureSession &&
    raw.fixtureSession.roles !== undefined &&
    !Array.isArray(raw.fixtureSession.roles)
  ) {
    return createBootstrapError(
      "ADMIN_BOOTSTRAP_PAYLOAD_INVALID",
      "Admin bootstrap payload invalid",
      "fixtureSession.roles must be an array when provided.",
    );
  }

  if ("getAccessToken" in raw && raw.getAccessToken !== undefined && !isAccessTokenProvider(raw.getAccessToken)) {
    return createBootstrapError(
      "ADMIN_BOOTSTRAP_PAYLOAD_INVALID",
      "Admin bootstrap payload invalid",
      "getAccessToken must be a function when provided.",
    );
  }

  return {
    runtimeConfig: isObject(raw.runtimeConfig) ? (raw.runtimeConfig as Partial<AdminDashboardRuntimeConfig>) : undefined,
    fixtureSession: normalizeFixtureSession(raw.fixtureSession),
    getAccessToken: isAccessTokenProvider(raw.getAccessToken) ? raw.getAccessToken : undefined,
  };
}

function shouldUseStandaloneFirebaseAuth(
  runtimeConfig: AdminDashboardRuntimeConfig,
  props: AdminDashboardAppProps,
): boolean {
  return (
    runtimeConfig.authMode === "admin-auth" &&
    runtimeConfig.authProvider === "firebase-google-popup" &&
    !isAccessTokenProvider(props.getAccessToken)
  );
}

function isCloudflarePagesHost(value: string): boolean {
  try {
    return new URL(value).hostname.endsWith(".pages.dev");
  } catch {
    return false;
  }
}

export function resolveAdminDashboardBootstrapError(
  props: AdminDashboardAppProps,
  runtimeConfig: AdminDashboardRuntimeConfig = resolveAdminDashboardRuntimeConfig(props.runtimeConfig),
): AdminDashboardBootstrapError | undefined {
  if (props.bootstrapError) {
    return props.bootstrapError;
  }

  if (shouldUseStandaloneFirebaseAuth(runtimeConfig, props)) {
    if (runtimeConfig.firebaseConfig) {
      if (isCloudflarePagesHost(runtimeConfig.apiBaseUrl)) {
        return {
          code: "ADMIN_AUTH_PROVIDER_CONFIG_INVALID",
          title: "Admin API base URL configuration missing",
          message:
            "Standalone Cloudflare Pages admin deploys must set VITE_ADMIN_API_BASE_URL to the Cloud Run service root.",
          details: [
            `apiBaseUrl=${runtimeConfig.apiBaseUrl}`,
            "Pages host itself does not proxy /api/admin/* in the current production contract.",
            "Set VITE_ADMIN_API_BASE_URL to the deployed API service root without the /api suffix.",
          ],
        };
      }

      return undefined;
    }

    return {
      code: "ADMIN_AUTH_PROVIDER_CONFIG_INVALID",
      title: "Admin auth provider configuration missing",
      message: "Standalone Firebase admin auth requires the full EXPO_PUBLIC_FIREBASE_* runtime config.",
      details: [
        `authMode=${runtimeConfig.authMode}`,
        `authProvider=${runtimeConfig.authProvider}`,
        "Required: EXPO_PUBLIC_FIREBASE_API_KEY, EXPO_PUBLIC_FIREBASE_APP_ID, EXPO_PUBLIC_FIREBASE_PROJECT_ID, EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN, EXPO_PUBLIC_FIREBASE_MESSAGING_SENDER_ID, EXPO_PUBLIC_FIREBASE_STORAGE_BUCKET",
      ],
    };
  }

  const validationError = validateAdminAuthSessionConfig({
    authMode: runtimeConfig.authMode,
    runtimeRoles: runtimeConfig.adminRoles,
    fixtureSession: props.fixtureSession,
    getAccessToken: props.getAccessToken,
  });

  if (!validationError) {
    return undefined;
  }

  return {
    code: validationError.code,
    title: "Admin auth bootstrap required",
    message: validationError.message,
    details: [
      `authMode=${runtimeConfig.authMode}`,
      `environment=${runtimeConfig.environmentName}`,
      "Host shell must inject runtimeConfig and an in-memory getAccessToken provider together.",
    ],
  };
}

function AdminBootstrapErrorScreen(props: { error: AdminDashboardBootstrapError }): JSX.Element {
  const { locale } = useAdminDashboardLocale();
  const display = resolveBootstrapErrorDisplay(props.error, locale);

  return (
    <main style={{ padding: "32px", maxWidth: "840px", margin: "0 auto" }}>
      <AdminErrorState
        title={display.title}
        body={display.body}
        tone="error"
        meta={buildLocalizedBootstrapErrorMeta(props.error, locale)}
      />
    </main>
  );
}

function StableAdminRouter(props: {
  runtimeConfig: AdminDashboardRuntimeConfig;
  baseProps: AdminDashboardAppProps;
}): JSX.Element {
  const dependencyError = useMemo(() => {
    try {
      createAdminDashboardDependencies(props.runtimeConfig, {
        fixtureSession: props.baseProps.fixtureSession,
        getAccessToken: props.baseProps.getAccessToken,
      });
      return undefined;
    } catch (error) {
      if (error instanceof AdminAuthSessionBootstrapError) {
        return error;
      }

      throw error;
    }
  }, [props.baseProps.fixtureSession, props.baseProps.getAccessToken, props.runtimeConfig]);

  if (dependencyError) {
    return (
      <AdminBootstrapErrorScreen
        error={{
          code: dependencyError.code,
          title: "Admin auth bootstrap required",
          message: dependencyError.message,
        }}
      />
    );
  }

  const dependencies = useMemo(
    () =>
      createAdminDashboardDependencies(props.runtimeConfig, {
        fixtureSession: props.baseProps.fixtureSession,
        getAccessToken: props.baseProps.getAccessToken,
      }),
    [props.baseProps.fixtureSession, props.baseProps.getAccessToken, props.runtimeConfig],
  );
  const router = useMemo(() => createBrowserRouter(createAdminDashboardRoutes(dependencies)), [dependencies]);
  return <RouterProvider router={router} />;
}

function ResolvedAdminDashboardApp(props: AdminDashboardAppProps & { runtimeConfig: AdminDashboardRuntimeConfig }): JSX.Element {
  const bootstrapError = resolveAdminDashboardBootstrapError(props, props.runtimeConfig);
  if (bootstrapError) {
    return <AdminBootstrapErrorScreen error={bootstrapError} />;
  }

  return <StableAdminRouter runtimeConfig={props.runtimeConfig} baseProps={props} />;
}

function AdminFirebaseAuthScreen(props: {
  snapshot: FirebaseAdminAuthSnapshot;
  onSignIn: () => void;
  onSignOut: () => void;
  runtimeConfig: AdminDashboardRuntimeConfig;
}): JSX.Element {
  const { locale } = useAdminDashboardLocale();
  const isWorking = props.snapshot.status === "loading";
  const hasNoRoles = props.snapshot.status === "signed_in" && props.snapshot.roles.length === 0;
  const copy = resolveFirebaseAuthScreenCopy(props.snapshot, locale);
  const rawReason =
    "message" in props.snapshot ? localizeFirebaseAuthReason(props.snapshot.message, locale) : undefined;

  return (
    <main style={{ padding: "32px", maxWidth: "840px", margin: "0 auto", display: "grid", gap: "16px" }}>
      <AdminErrorState
        title={copy.title}
        body={copy.body}
        tone={props.snapshot.status === "error" || hasNoRoles ? "error" : undefined}
        meta={[
          {
            key: "auth_mode",
            label: copy.authModeLabel,
            value: copy.authModeValue,
          },
          {
            key: "session_state",
            label: copy.sessionLabel,
            value: copy.sessionStateValue,
          },
          {
            key: "bundle_fingerprint",
            label: locale === "ko" ? "번들" : "Bundle",
            value: props.runtimeConfig.bundleFingerprint,
          },
          ...(rawReason
            ? [
                {
                  key: "raw_reason",
                  label: copy.rawReasonLabel,
                  value: rawReason,
                },
              ]
            : []),
        ]}
      />
      <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={props.onSignIn}
          disabled={isWorking}
          style={{ padding: "12px 16px", borderRadius: "12px", border: "1px solid #1f2937", background: "#111827", color: "#fff" }}
        >
          {isWorking ? copy.signingInAction : copy.signInAction}
        </button>
        {(props.snapshot.status === "signed_in" || props.snapshot.status === "error") && (
          <button
            type="button"
            onClick={props.onSignOut}
            style={{ padding: "12px 16px", borderRadius: "12px", border: "1px solid #d1d5db", background: "#fff", color: "#111827" }}
          >
            {copy.resetSession}
          </button>
        )}
      </div>
    </main>
  );
}

function StandaloneFirebaseAdminAuthBoundary(props: {
  baseProps: AdminDashboardAppProps;
  runtimeConfig: AdminDashboardRuntimeConfig;
}): JSX.Element {
  const [provider, setProvider] = useState<FirebaseWebAdminAuthProvider | null>(null);
  const [snapshot, setSnapshot] = useState<FirebaseAdminAuthSnapshot>({
    status: "loading",
    roles: [],
  });

  useEffect(() => {
    let active = true;
    let unsubscribe: (() => void) | undefined;

    setSnapshot({
      status: "loading",
      roles: [],
    });

    void createFirebaseWebAdminAuthProvider(props.runtimeConfig.firebaseConfig!)
      .then((createdProvider) => {
        if (!active) {
          return;
        }

        setProvider(createdProvider);
        unsubscribe = createdProvider.subscribe((nextSnapshot) => {
          if (active) {
            setSnapshot(nextSnapshot);
          }
        });
      })
      .catch((error) => {
        if (!active) {
          return;
        }

        setSnapshot({
          status: "error",
          roles: [],
          message: error instanceof Error ? error.message : "Failed to initialize Firebase admin auth.",
        });
      });

    return () => {
      active = false;
      unsubscribe?.();
    };
  }, [props.runtimeConfig.firebaseConfig]);

  if (snapshot.status === "signed_in" && snapshot.roles.length > 0 && provider) {
    return (
      <ResolvedAdminDashboardApp
        {...props.baseProps}
        getAccessToken={provider.getAccessToken}
        runtimeConfig={{
          ...props.runtimeConfig,
          adminRoles: snapshot.roles,
        }}
      />
    );
  }

  return (
    <AdminFirebaseAuthScreen
      snapshot={snapshot}
      runtimeConfig={props.runtimeConfig}
      onSignIn={() => {
        if (!provider) {
          return;
        }

        setSnapshot({
          status: "loading",
          roles: snapshot.roles,
        });

        void provider.signIn().catch((error) => {
          setSnapshot({
            status: "error",
            roles: [],
            message: error instanceof Error ? error.message : "Google popup sign-in failed.",
          });
        });
      }}
      onSignOut={() => {
        if (!provider) {
          return;
        }

        void provider.signOut().catch((error) => {
          setSnapshot({
            status: "error",
            roles: [],
            message: error instanceof Error ? error.message : "Firebase sign-out failed.",
          });
        });
      }}
    />
  );
}

export function AdminDashboardApp(props: AdminDashboardAppProps): JSX.Element {
  const runtimeConfig = resolveAdminDashboardRuntimeConfig(props.runtimeConfig);
  const [locale, setLocale] = useState<AdminDashboardLocale>(() => resolveInitialLocaleState());

  useEffect(() => {
    persistAdminDashboardLocale(getWindowStorage(), locale);
  }, [locale]);

  return (
    <AdminDashboardLocaleProvider
      value={{
        locale,
        setLocale,
      }}
    >
      {shouldUseStandaloneFirebaseAuth(runtimeConfig, props) ? (
        (() => {
          const bootstrapError = resolveAdminDashboardBootstrapError(props, runtimeConfig);
          if (bootstrapError) {
            return <AdminBootstrapErrorScreen error={bootstrapError} />;
          }

          return <StandaloneFirebaseAdminAuthBoundary baseProps={props} runtimeConfig={runtimeConfig} />;
        })()
      ) : (
        <ResolvedAdminDashboardApp {...props} runtimeConfig={runtimeConfig} />
      )}
    </AdminDashboardLocaleProvider>
  );
}
